import React, { useMemo } from "react";
import { Alert, Button, Collapse, Empty, Modal, Space, Table, Tag, Tooltip, Typography } from "antd";
import { CheckCircleFilled, CloseCircleFilled, ExclamationCircleFilled, InfoCircleFilled } from "@ant-design/icons";
import commonFn from "@/utils/commonFn";

const { Text } = Typography;
const currency = (value) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2 }).format(Number(value || 0));

function StatusChip({ tone, children }) {
  const Icon = tone === "error" ? CloseCircleFilled : tone === "warning" ? ExclamationCircleFilled : tone === "processing" ? InfoCircleFilled : CheckCircleFilled;
  return <span className={`monthly-worker-ap-status-chip is-${tone}`}><Icon />{children}</span>;
}

function SalaryFormula({ record }) {
  const calculations = record?.salaryCalculations || [];
  return <div className="monthly-worker-ap-formula-tooltip">
    <div className="monthly-worker-ap-formula-tooltip-header"><strong>Payroll calculation</strong><span>{record?.vendorName || "Vendor"}</span></div>
    {!calculations.length ? <span className="monthly-worker-ap-formula-empty">No overlapping salary contract is available in this saved pre-check.</span> : <>
      <code className="monthly-worker-ap-formula-rule">Σ [(monthly amount ÷ 2) × (overlap days ÷ days in period)]</code>
      <div className="monthly-worker-ap-formula-terms">{calculations.map((item, index) => <div className="monthly-worker-ap-formula-term" key={item.salaryAdjustmentId || index}>
        <span>Contract {index + 1} · {item.effectiveStartDate} to {item.effectiveEndDate}</span>
        <code>({currency(item.monthlyAmount)} ÷ 2) × ({item.overlapDays} ÷ {item.periodDays}) = {currency(item.calculatedAmount)}</code>
      </div>)}</div>
      <div className="monthly-worker-ap-formula-total"><span>Vendor total</span><strong>Σ = {currency(record.payAmount)}</strong></div>
    </>}
  </div>;
}

const previewGroups = [
  { key: "normalItems", title: "Ready", color: "success", description: "Positive payroll records available for period construction." },
  { key: "zeroAmountItems", title: "Zero payroll", color: "default", description: "Archived for review, but excluded from vendor bills." },
  { key: "negativeAmountItems", title: "Negative payroll", color: "error", description: "Correct the salary adjustments before building this period." },
  { key: "noSalaryItems", title: "No salary contract", color: "warning", description: "No active salary range overlaps the selected period." },
  { key: "noJobItems", title: "No eligible work order", color: "warning", description: "No maintenance work order qualifies for payroll allocation." },
  { key: "clockWarningItems", title: "Clock-in review", color: "processing", description: "Clock-in exceptions require review before period construction." },
];

const columns = [
  { title: "Vendor", dataIndex: "vendorName", width: 180 },
  { title: "Vendor Code", dataIndex: "vendid", width: 120 },
  {
    title: "Status",
    dataIndex: "vendorStatus",
    width: 95,
    render: (value) => <Tag color={String(value).toUpperCase() === "INACTIVE" || value === "I" ? "red" : "green"}>{value || "-"}</Tag>,
  },
  { title: "Supervisor", dataIndex: "supervisorName", width: 160 },
  { title: "Pay Amount", dataIndex: "payAmount", width: 140, render: (value, record) => <Tooltip placement="top" mouseEnterDelay={0.15} overlayClassName="monthly-worker-ap-formula-overlay" title={<SalaryFormula record={record} />}><span className="monthly-worker-ap-pay-amount" tabIndex={0}>{commonFn.amountToDollarFn(value)}</span></Tooltip> },
  { title: "Jobs", dataIndex: "jobCount", width: 75 },
  { title: "Clock-ins", dataIndex: "clockCount", width: 90 },
  { title: "Supplemented", dataIndex: "supplementedClockCount", width: 105 },
  {
    title: "Notes",
    dataIndex: "warnings",
    render: (warnings) => (warnings || []).length ? (warnings || []).map((warning) => <Tag color="gold" key={warning}>{warning}</Tag>) : "-",
  },
];

export default function PreviewModal({ open, preview, onCancel, canConfirm, confirmLoading, onConfirm }) {
  const hasNegativePayroll = (preview?.negativeAmountItems || []).length > 0;
  const activeGroups = useMemo(
    () => previewGroups.filter((group) => (preview?.[group.key] || []).length > 0),
    [preview]
  );

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      footer={<Space><Button onClick={onCancel}>Close</Button>{canConfirm && <Button type="primary" loading={confirmLoading} disabled={hasNegativePayroll} onClick={onConfirm}>Confirm pre-check</Button>}</Space>}
      width="calc(100vw - 64px)"
      destroyOnClose
      title="Payroll pre-check"
      className="monthly-worker-ap-preview-modal"
    >
      <div className="monthly-worker-ap-preview-summary">
        <div className="monthly-worker-ap-preview-summary-card"><Text type="secondary">Pay period</Text><Text strong>{preview?.payStartDate || "-"} ~ {preview?.payEndDate || "-"}</Text></div>
        <div className="monthly-worker-ap-preview-summary-card"><Text type="secondary">Result</Text><StatusChip tone={hasNegativePayroll ? "error" : "success"}>{hasNegativePayroll ? "Generation blocked" : "Ready for review"}</StatusChip></div>
      </div>

      <div className="monthly-worker-ap-pay-formula">
        <div><Text strong>Payroll calculation</Text><Text type="secondary">Each overlapping salary contract is prorated within the selected half-month period, then combined for the vendor.</Text></div>
        <code>Σ [(monthly amount ÷ 2) × (overlap days ÷ days in period)]</code>
        <Text type="secondary">The vendor total is allocated evenly across eligible work orders. Final values are rounded to two decimal places.</Text>
      </div>

      {hasNegativePayroll && (
        <Alert
          type="error"
          showIcon
          message="Negative payroll was found"
          description="Correct the overlapping salary adjustments before generating this payroll period."
          className="monthly-worker-ap-preview-alert"
        />
      )}

      {canConfirm && !hasNegativePayroll && <Alert className="monthly-worker-ap-preview-confirmation-note" type="info" showIcon message="Review complete?" description="Confirming this pre-check records the reviewer and time, then unlocks period construction." />}

      {!activeGroups.length ? <Empty description="No monthly workers were found for this period." /> : <Collapse
        className="monthly-worker-ap-preview-collapse"
        defaultActiveKey={activeGroups.slice(0, 2).map((group) => group.key)}
        items={activeGroups.map((group) => {
          const rows = preview?.[group.key] || [];
          return {
            key: group.key,
            label: <div className="monthly-worker-ap-preview-heading"><div><StatusChip tone={group.color}>{group.title}</StatusChip><span>{group.description}</span></div><Tag>{rows.length}</Tag></div>,
            children: <Table rowClassName="table-row-gradient" size="small" columns={columns} dataSource={rows} rowKey={(row) => `${group.key}-${row.vendorId}`} pagination={{ pageSize: 10, showSizeChanger: false, size: "small" }} scroll={{ x: "max-content" }} />,
          };
        })}
      />}
    </Modal>
  );
}
